import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { verifyCodeAndLogin, clearError } from '../store/authSlice';
import {
  clearPostLoginStorage,
  resolvePostLoginPath,
} from '../utils/onboardingNavigation';
import Particles from '../components/common/Particles';
import { loginVerifyCodeConfig, getOptimizedConfig } from '../components/common/ParticleConfig';
import { BrowserSupportIndicator } from '../components/common/BrowserSupportIndicator';
import { vibeService } from '../services/VibeService';
import { MAINTENANCE_CONFIG, isAllowedBetaEmail } from '../utils/constants';
import './Login.css';

const LoginVerifyCode: React.FC = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const redirectStartedRef = useRef(false);
  
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  const redirectAfterLogin = async () => {
    if (redirectStartedRef.current) return;
    redirectStartedRef.current = true;
    setIsRedirecting(true);
    clearPostLoginStorage();
    const path = await resolvePostLoginPath();
    navigate(path);
  };

  // Initialize email and demo mode from sessionStorage
  useEffect(() => {
    const storedEmail = sessionStorage.getItem('loginEmail');
    const demoMode = sessionStorage.getItem('demoMode') === 'true';

    if (!storedEmail) {
      navigate(MAINTENANCE_CONFIG.ENABLED ? '/' : '/login');
      return;
    }

    if (
      MAINTENANCE_CONFIG.ENABLED &&
      !isAllowedBetaEmail(storedEmail)
    ) {
      sessionStorage.removeItem('loginEmail');
      sessionStorage.removeItem('demoMode');
      navigate('/');
      return;
    }

    setEmail(storedEmail);
    setIsDemoMode(demoMode);
  }, [navigate]);

  // Handle returning users who land here while already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      redirectAfterLogin();
    }
  }, [isAuthenticated]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Clear error when user starts typing
    if (error) {
      dispatch(clearError());
    }

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle Enter key
    if (e.key === 'Enter') {
      handleCodeSubmit();
    }
  };

  const handleCodeSubmit = async () => {
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      return;
    }

    // In demo mode, accept the demo code and navigate
    if (isDemoMode && fullCode === '123456') {
      vibeService.trigger('celebration');
      await redirectAfterLogin();
      return;
    }

    try {
      await dispatch(verifyCodeAndLogin({ email, code: fullCode })).unwrap();
      vibeService.trigger('celebration');
      await redirectAfterLogin();
    } catch (err) {
      console.error('Failed to verify code:', err);
      
      // In demo mode, if any error occurs, still allow navigation with demo code
      if (isDemoMode && fullCode === '123456') {
        vibeService.trigger('celebration');
        await redirectAfterLogin();
      } else {
        vibeService.trigger('error');
      }
    }
  };

  const handleBackToEmail = () => {
    sessionStorage.removeItem('loginEmail');
    sessionStorage.removeItem('demoMode');
    dispatch(clearError());
    navigate(MAINTENANCE_CONFIG.ENABLED ? '/' : '/login');
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  // Don't render if email is not available
  if (!email) {
    return null;
  }

  // Get optimized particle configuration for this device
  const particleConfig = getOptimizedConfig(loginVerifyCodeConfig);

  // Show full-screen loader during verification or redirection
  if (isLoading || isRedirecting) {
    return (
      <div className="simple-login-page" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Particle Background */}
        <Particles {...particleConfig} />
        
        <div className="login-content">
          {/* Logo */}
          <div className="logo">
            <img src="/image_i_do1.png" alt="Luci logo" className="luci-logo" />
          </div>

          <div className="verification-loader">
            <div className="loader-spinner"></div>
            <p className="loader-text">
              {isRedirecting ? 'Signing you in...' : 'Verifying code...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="simple-login-page" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Particle Background */}
      <Particles {...particleConfig} />
      
      <div className="login-content">
        {/* Logo */}
        <div className="logo">
          <img src="/image_i_do1.png" alt="Luci logo" className="luci-logo" />
        </div>

        
        <p className="instructions">
          Enter the code sent to<br />
          <strong>{email}</strong>
        </p>

        {isDemoMode && (
          <div className="demo-notice" style={{
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '4px',
            padding: '12px',
            margin: '16px 0',
            fontSize: '14px',
            color: '#1976d2'
          }}>
            <strong>Demo Mode:</strong> Use code <strong>123456</strong> to continue
          </div>
        )}

        <div className="code-input-container">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => { inputRefs.current[index] = el; }}
              type="text"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className="code-input"
              disabled={isLoading}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && (
          <div className="error-text">
            {error}
          </div>
        )}

        <button
          onClick={handleCodeSubmit}
          className="submit-button"
          disabled={isLoading || code.join('').length !== 6}
        >
          Verify Code
        </button>

        <button
          onClick={handleBackToEmail}
          className="back-to-email-link"
          disabled={isLoading}
        >
          Enter email id again
        </button>

        <p className="help-text">
          Can't find the email? Check your spam folder.
        </p>
      </div>

      <BrowserSupportIndicator />
    </div>
  );
};

export default LoginVerifyCode;
